import { MsText } from '@sunrise/components'
import { commonUtils, componentUtils, getCmsProperty, useGlobalStore } from '@sunrise/shared'
import { Cms } from '@sunrise/types'
import { computed, defineComponent, PropType } from 'vue'
import CsChannelTeaser from '../../../components/CsChannelTeaser/CsChannelTeaser.vue'
import { ChannelType, CountableChannelType, CountList } from './M_ChannelTeaser.interface'

export default defineComponent({
    name: 'M_ChannelTeaser',
    components: {
        CsChannelTeaser,
        MsText,
    },
    props: {
        cmsContent: { type: Object as PropType<Cms.Content<CountList[]>>, required: true },
    },
    setup(props) {
        const requestContext = useGlobalStore().requestContext
        const countList = computed(() => props.cmsContent.serviceData)
        const channel = getCmsProperty('meinestadt/types#channelTeaserChannel', props.cmsContent) as ChannelType
        const count = countList?.value?.find((count) => count.channel === channel)?.count
        const linkAttributes = componentUtils.getLinkAttributes(props.cmsContent)
        const locationName = requestContext?.location.name

        const image = componentUtils.getChildByLabel('Image', props.cmsContent)
        const cmsTextContent = componentUtils.getChildByLabel('Text', props.cmsContent)?.text

        const canShowCountText = count && isCountable(channel)
        const countText = canShowCountText && locationName ? getCountText(channel, count, locationName) : null

        return {
            countList,
            channel,
            image,
            cmsTextContent,
            countText,
            linkAttributes,
            count,
        }
    },
})

function getCountText(countableChannelType: CountableChannelType, count: number, locationName: string) {
    const isPlural = count !== 1

    if (isPlural) {
        return createPluralText(countableChannelType, count, locationName)
    }

    return createSingularText(countableChannelType, locationName)
}

function createSingularText(countableChannelType: CountableChannelType, locationName: string) {
    let channelLabel: string
    switch (countableChannelType) {
        case CountableChannelType.Auto:
            channelLabel = 'Fahrzeug'
            break
        case CountableChannelType.Immo:
            channelLabel = 'Immobilie'
            break
        case CountableChannelType.Jobs:
            channelLabel = 'freie Stelle'
            break
    }

    return `1 ${channelLabel} in ${locationName === 'Deutschland' ? locationName : locationName + ' und Umgebung'}`
}

function createPluralText(countableChannelType: CountableChannelType, count: number, locationName: string) {
    const countWithSeparator = commonUtils.thousandSeparator(count)

    let channelLabel: string

    switch (countableChannelType) {
        case CountableChannelType.Auto:
            channelLabel = 'Fahrzeuge'
            break
        case CountableChannelType.Immo:
            channelLabel = 'Immobilien'
            break
        case CountableChannelType.Jobs:
            channelLabel = 'freie Stellen'
            break
    }

    return `${countWithSeparator} ${channelLabel} in ${
        locationName === 'Deutschland' ? locationName : locationName + ' und Umgebung'
    }`
}

export const isCountable = (channel: ChannelType): channel is CountableChannelType =>
    Object.values(CountableChannelType).includes(channel as CountableChannelType)
